import React, { useState, useMemo } from 'react';
import { Translation } from '../types';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface Props {
  t: Translation;
}

export const Calculator: React.FC<Props> = ({ t }) => {
  const [dailyPrice, setDailyPrice] = useState(3500);
  const [occupancy, setOccupancy] = useState(60);
  const houseCost = 1200000; // CZK

  const annualRevenue = useMemo(() => {
    return Math.round(dailyPrice * 365 * (occupancy / 100));
  }, [dailyPrice, occupancy]);

  const paybackPeriod = useMemo(() => {
    return annualRevenue > 0 ? (houseCost / annualRevenue).toFixed(1) : '∞';
  }, [annualRevenue]);

  const chartData = useMemo(() => {
    const data = [];
    let accumulated = -houseCost;
    for (let year = 0; year <= 5; year++) {
      data.push({
        year: `Y${year}`,
        balance: Math.round(accumulated / 1000)
      });
      accumulated += annualRevenue;
    }
    return data;
  }, [annualRevenue]);

  return (
    <div id="investment" className="py-24 bg-stone-900 text-white px-6">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl lg:text-5xl font-light mb-12 text-center">{t.calculator.title}</h2>
        
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-12">
            <div className="space-y-6">
              <label className="block">
                <span className="text-stone-400 block mb-2 font-bold uppercase tracking-widest text-[10px]">{t.calculator.dailyPrice}: {dailyPrice} CZK</span>
                <input 
                  type="range" 
                  min="1000" 
                  max="10000" 
                  step="100" 
                  value={dailyPrice}
                  onChange={(e) => setDailyPrice(Number(e.target.value))}
                  className="w-full h-1 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-white"
                />
              </label>

              <label className="block">
                <span className="text-stone-400 block mb-2 font-bold uppercase tracking-widest text-[10px]">{t.calculator.occupancy}: {occupancy}%</span>
                <input 
                  type="range" 
                  min="10" 
                  max="100" 
                  step="5" 
                  value={occupancy}
                  onChange={(e) => setOccupancy(Number(e.target.value))}
                  className="w-full h-1 bg-stone-700 rounded-lg appearance-none cursor-pointer accent-white"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-8 border-t border-stone-800 pt-8">
              <div>
                <span className="text-stone-400 text-[10px] uppercase tracking-widest font-bold">{t.calculator.totalRevenue}</span>
                <p className="text-3xl font-light mt-2">{annualRevenue.toLocaleString()} CZK</p>
              </div>
              <div>
                <span className="text-stone-400 text-[10px] uppercase tracking-widest font-bold">{t.calculator.payback}</span>
                <p className="text-3xl font-light mt-2 text-emerald-400">{paybackPeriod} {Number(paybackPeriod) < 2 ? 'year' : 'years'}</p>
              </div>
            </div>
          </div>

          {/* Hardcoded height ensures Recharts has dimensions even if Tailwind CSS loading is delayed */}
          <div className="relative w-full bg-stone-800/50 p-6 rounded-2xl border border-white/5" style={{ height: '400px', minHeight: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                <XAxis dataKey="year" stroke="#666" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#666" fontSize={10} unit="k" tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1c1917', border: 'none', borderRadius: '8px', fontSize: '12px', color: '#fff' }}
                  itemStyle={{ color: '#10b981' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="balance" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  fillOpacity={0.3} 
                  fill="#10b981" 
                  animationDuration={1000}
                />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-stone-500 mt-4 text-center uppercase tracking-widest font-bold">ROI PROJECTION (k CZK)</p>
          </div>
        </div>
      </div>
    </div>
  );
};